import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, AlertTriangle, Sparkles, Loader2, Archive, Users, Wine } from "lucide-react";
import { useClients, useClientStats, useClientsMissingDropboxFolders, useBatchAutoMatchDropboxFolders, Client, BatchAutoMatchResults, useArchivedClients, ClientsMissingDropboxFolder } from "@/hooks/useClients";
import { useDropboxClientFolders } from "@/hooks/useDropboxFolders";
import { useDocumentFacilities, Facility } from "@/hooks/useFacilities";
import { useIsAdmin } from "@/hooks/useUserProfile";
import { CreateClientDialog } from "@/components/clients/CreateClientDialog";
import { ClientDetailSheet } from "@/components/clients/ClientDetailSheet";
import { CreateLotDialog } from "@/components/lots/CreateLotDialog";
import { DropboxFolderDialog } from "@/components/clients/DropboxFolderDialog";
import { AutoMatchResultsDialog } from "@/components/clients/AutoMatchResultsDialog";
import { LotsTab } from "@/components/clients/LotsTab";
import { Skeleton } from "@/components/ui/skeleton";
import { useVarietyColorMap, getVarietyColor } from "@/hooks/useVarietyColorMap";

interface FacilityAutoMatchButtonProps {
  facility: Facility;
  missing: ClientsMissingDropboxFolder | undefined;
  isMatching: boolean;
  onAutoMatch: (facility: Facility, folders: { name: string; path: string }[], missingClients: { id: string; name: string; code: string | null }[]) => void;
}

// One instance per Documents-enabled facility -- each owns its own Dropbox
// folder-list fetch (cheap: useDropboxClientFolders shares one cached
// request across facilities) so this scales to any number of facilities
// without hand-duplicating a hook call per facility in the parent.
function FacilityAutoMatchButton({ facility, missing, isMatching, onAutoMatch }: FacilityAutoMatchButtonProps) {
  const { data: folders, isLoading, isError } = useDropboxClientFolders(facility.id);

  if (!missing || missing.count === 0) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAutoMatch(facility, folders || [], missing.clients)}
            disabled={isMatching || isLoading || !folders || folders.length === 0}
            className="gap-2"
          >
            {isMatching || isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Auto-Match {facility.code} Folders
            <Badge variant="secondary" className="ml-1">
              {missing.count}
            </Badge>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isLoading ? (
            <p>Loading Dropbox folders...</p>
          ) : isError || !folders || folders.length === 0 ? (
            <p>{facility.code} Dropbox folders unavailable. Check Dropbox integration in Settings.</p>
          ) : (
            <p>Automatically match {facility.code} Dropbox folders for {missing.count} clients</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ClientCardProps {
  client: Client;
  missingFacilities: Facility[];
  onSetDropboxFolder?: (client: Client, facility: Facility) => void;
}

function ClientCard({ client, missingFacilities, onSetDropboxFolder }: ClientCardProps) {
  const { data: stats, isLoading } = useClientStats(client.id);
  const colorMap = useVarietyColorMap();

  const contactName = [client.first_name, client.last_name].filter(Boolean).join(' ');

  return (
    <Card className="transition-all hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{client.name}</CardTitle>
            {contactName && (
              <p className="text-xs text-muted-foreground mt-1">Contact: {contactName}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {missingFacilities.map((facility) => (
              <TooltipProvider key={facility.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 gap-1 text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDropboxFolder?.(client, facility);
                      }}
                    >
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span className="text-[10px] font-medium">{facility.code}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{facility.code} Dropbox folder not mapped</p>
                    <p className="text-xs text-muted-foreground">Click to set</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            {client.is_active === false ? (
              <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                archived
              </Badge>
            ) : (
              <Badge variant="default" className="bg-success text-success-foreground">
                active
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-primary">
                  {stats?.activeLots || 0}
                </p>
                <p className="text-xs text-muted-foreground">Active Lots</p>
              </div>
              <div>
                <p className="text-sm font-semibold">
                  {stats?.totalVolume || 0} L
                </p>
                <p className="text-xs text-muted-foreground">Total Volume</p>
              </div>
            </div>
            {stats?.varietals && stats.varietals.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                {stats.varietals.map((v) => (
                  <Badge
                    key={v}
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0 gap-1"
                    style={{
                      backgroundColor: getVarietyColor(colorMap, v) + "20",
                      color: getVarietyColor(colorMap, v),
                      borderColor: getVarietyColor(colorMap, v) + "40",
                    }}
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: getVarietyColor(colorMap, v) }}
                    />
                    {v}
                  </Badge>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function Clients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "clients";

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dropboxFolderClient, setDropboxFolderClient] = useState<Client | null>(null);
  const [dropboxFolderFacility, setDropboxFolderFacility] = useState<Facility | null>(null);
  const [dropboxDialogOpen, setDropboxDialogOpen] = useState(false);
  const [autoMatchResults, setAutoMatchResults] = useState<BatchAutoMatchResults | null>(null);
  const [autoMatchDialogOpen, setAutoMatchDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const isAdmin = useIsAdmin();
  const { data: clients = [], isLoading: clientsLoading } = useClients(searchTerm);
  const { data: archivedClients = [], isLoading: archivedLoading } = useArchivedClients(searchTerm);
  const { data: documentFacilities = [] } = useDocumentFacilities();
  const facilityIds = useMemo(() => documentFacilities.map((f) => f.id), [documentFacilities]);
  const { data: missingByFacility } = useClientsMissingDropboxFolders(facilityIds);
  const batchAutoMatch = useBatchAutoMatchDropboxFolders();

  // Map of clientId -> the facilities that client is missing a Dropbox folder mapping for.
  const missingByClient = useMemo(() => {
    const map = new Map<string, Facility[]>();
    if (!missingByFacility) return map;
    for (const facility of documentFacilities) {
      for (const c of missingByFacility.get(facility.id)?.clients ?? []) {
        const arr = map.get(c.id) || [];
        arr.push(facility);
        map.set(c.id, arr);
      }
    }
    return map;
  }, [documentFacilities, missingByFacility]);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const handleSetDropboxFolder = (client: Client, facility: Facility) => {
    setDropboxFolderClient(client);
    setDropboxFolderFacility(facility);
    setDropboxDialogOpen(true);
  };

  const handleBatchAutoMatch = (facility: Facility, folders: { name: string; path: string }[], missingClients: { id: string; name: string; code: string | null }[]) => {
    if (missingClients.length === 0 || !facility.dropbox_folder_prefix) return;
    batchAutoMatch.mutate(
      {
        clients: missingClients,
        folders,
        facilityId: facility.id,
        dropboxFolderPrefix: facility.dropbox_folder_prefix,
      },
      {
        onSuccess: (results) => {
          setAutoMatchResults(results);
          setAutoMatchDialogOpen(true);
        },
      }
    );
  };

  const handleViewDetails = (client: Client) => {
    setSelectedClient(client);
    setSheetOpen(true);
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Clients & Lots</h1>
        </div>
        <div className="flex items-center gap-2">
          <CreateLotDialog />
          <CreateClientDialog />
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="clients" className="gap-2">
              <Users className="h-4 w-4" />
              Clients
            </TabsTrigger>
            <TabsTrigger value="lots" className="gap-2">
              <Wine className="h-4 w-4" />
              Lots
            </TabsTrigger>
          </TabsList>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">
                  {showArchived ? "Archived Clients" : "Active Clients"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {showArchived
                    ? `${archivedClients.length} archived clients`
                    : `${clients.length} clients with active operations`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={showArchived ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowArchived(!showArchived)}
                  className="gap-2"
                >
                  <Archive className="h-4 w-4" />
                  {showArchived ? "View Active" : "View Archived"}
                  {!showArchived && archivedClients.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {archivedClients.length}
                    </Badge>
                  )}
                </Button>
                {!showArchived && isAdmin && documentFacilities.map((facility) => (
                  <FacilityAutoMatchButton
                    key={facility.id}
                    facility={facility}
                    missing={missingByFacility?.get(facility.id)}
                    isMatching={batchAutoMatch.isPending}
                    onAutoMatch={handleBatchAutoMatch}
                  />
                ))}
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search clients..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {(showArchived ? archivedLoading : clientsLoading) ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-48 w-full" />
                ))}
              </div>
            ) : (showArchived ? archivedClients : clients).length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground mb-4">
                  {showArchived ? "No archived clients" : "No clients found"}
                </p>
                {!showArchived && <CreateClientDialog />}
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
                {(showArchived ? archivedClients : clients).map((client) => (
                  <div key={client.id} onClick={() => handleViewDetails(client)} className="cursor-pointer">
                    <ClientCard
                      client={client}
                      missingFacilities={showArchived || !isAdmin ? [] : (missingByClient.get(client.id) || [])}
                      onSetDropboxFolder={showArchived || !isAdmin ? undefined : handleSetDropboxFolder}
                    />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Lots Tab */}
          <TabsContent value="lots">
            <LotsTab />
          </TabsContent>
        </Tabs>
      </div>

      <ClientDetailSheet
        client={selectedClient}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onClientUpdated={setSelectedClient}
      />

      <DropboxFolderDialog
        open={dropboxDialogOpen}
        onOpenChange={setDropboxDialogOpen}
        client={dropboxFolderClient}
        facility={dropboxFolderFacility}
      />

      <AutoMatchResultsDialog
        open={autoMatchDialogOpen}
        onOpenChange={setAutoMatchDialogOpen}
        results={autoMatchResults}
      />
    </div>
  );
}
